import _                  from 'underscore';
import React, { FC }      from 'react';
import moment, { Moment } from 'moment';
import { pluralize }      from 'apollo/lib/utils';

import { formatOption }           from '@src/util';
import { PreSelectedSelectField } from '@src/types';
import { TimeService }            from '@src/services';
import { SelectField }            from '@src//views/components/forms';


interface Props extends PreSelectedSelectField {

    /* What is the max range we can iterate steps to?*/
    maxOffset?: number,

    /* Which milestone should we be relative to? Affects ranges when the milestone is near the map boundaries */
    relativeMilestone: string,

    /* What is actual the milestone date value? */
    relativeMilestoneDate?: string | Moment,

    /* How far backwards and forwards offset is iterated to; eg [5, 10] would -5 steps back, +10 steps forward */
    relativeMilestoneOffsetBounds?: [ maxBackwardRange: null | number, maxForwardRange: null | number ],

    /* Allow the max offset to appear, even with boundary dates set */
    relativeMilestoneOffsetsRespectMaxOffset?: boolean,

    /* Dates that box the offset values; values outside these dates won't be offered */
    offsetBoundaryDates?: [ lowerBoundary: string | Moment, upperBoundary: string | Moment ],
}

const defaultProps: Props = {
    maxOffset:                                24,
    relativeMilestone:                        null,
    relativeMilestoneDate:                    null,
    relativeMilestoneOffsetBounds:            null,
    offsetBoundaryDates:                      null,
    relativeMilestoneOffsetsRespectMaxOffset: false,
}

const Dropdown: FC<Props> = ( _props ) => {

    let {
            maxOffset,
            relativeMilestone,
            relativeMilestoneDate,
            relativeMilestoneOffsetBounds,
            relativeMilestoneOffsetsRespectMaxOffset,
            offsetBoundaryDates,
            ...props
        } = _props;

    const hasMilestoneDate = () => {

        return !_.isEmpty( relativeMilestoneDate );
    }

    const getMilestoneDate = () => {

        return getDateAsMoment( relativeMilestoneDate );
    }

    const getDateAsMoment = ( date: string | Moment ) => {

        return !moment.isMoment( date ) ? TimeService.ISOtoMoment( date ) : date;
    }

    const getTimeboxedRange = () => {

        let maxBackwardsOffset = maxOffset,
            maxForwardsOffset  = maxOffset;

        // did we limit the offset in either direction?
        if( relativeMilestoneOffsetBounds ){

            let [ maxBackwardRange, maxForwardRange ] = relativeMilestoneOffsetBounds;

            maxBackwardsOffset = maxBackwardRange ? relativeMilestoneOffsetsRespectMaxOffset ? _.min( [ maxBackwardRange, maxOffset ] ) : maxBackwardRange : maxOffset;
            maxForwardsOffset = maxForwardRange ? relativeMilestoneOffsetsRespectMaxOffset ? _.min( [ maxForwardRange, maxOffset ] ) : maxForwardRange : maxOffset;
        }

        let range = _.range( -1 * maxBackwardsOffset, ( maxForwardsOffset + 1 ) );

        return range;
    }

    const getNumericLabel = ( n: number ) => {

        let isPositive = n > 0,
            isZero     = n == 0,
            labelText  = pluralize( n, 'Day', 'Days' ),
            label      = isPositive ? `+${ labelText }` : isZero ? 'None' : labelText;

        return label;
    }

    const getFormattedOptions = ( range: number[] ) => {

        if( hasMilestoneDate() ){

            let milestoneDate          = getMilestoneDate(),
                minDate: Moment | null = null,
                maxDate: Moment | null = null;

            // did we limit the dates in either direction?
            if( offsetBoundaryDates ){

                let [ lowerBoundaryDate, upperBoundaryDate ] = offsetBoundaryDates;

                minDate = lowerBoundaryDate ? getDateAsMoment( lowerBoundaryDate ) : null;
                maxDate = upperBoundaryDate ? getDateAsMoment( upperBoundaryDate ) : null;
            }

            // return all options available, removing invalid options on the range
            return _.compact( range.map( n => {

                let offsetDate       = milestoneDate.clone().add( n, 'month' ),
                    yearOffsetExists = offsetDate.year(),
                    label            = getNumericLabel( n );

                // If offset is negative, and the offsetDate is past the min date we care about, do not produce an option.
                if( n < 0 && minDate && offsetDate.isBefore( minDate ) ) return null;

                // If offset is positive, and the offsetDate is past the max date we care about, do not produce an option.
                if( n > 0 && maxDate && offsetDate.isAfter( maxDate ) ) return null;

                return formatOption( n, label, { group: String( yearOffsetExists ) } )

            } ) );

        }

        return range.map( n => formatOption( n, getNumericLabel( n ) ) );
    }

    let options = getFormattedOptions( getTimeboxedRange() );

    return (
        <SelectField
            search
            { ...props }
            useGroupNames={ hasMilestoneDate() }
            options={ options }>
        </SelectField>
    );
};

Dropdown.defaultProps = defaultProps;

export { Dropdown as ProjectTimeMilestoneOffsetDropdown };
